import { useTranslation } from 'react-i18next';
import type { LiveSortMode } from '@/domain/live-sort';
import { cn } from '@/lib/cn';

export interface LiveSortSwitchProps {
  value: LiveSortMode;
  onChange: (mode: LiveSortMode) => void;
  className?: string;
}

/**
 * Compact Time / Competition sort toggle for the Live page toolbar
 * (legacy `sortbySwicth` / `data-section="livesortby"`).
 *
 * Two end-aligned knobs in a pill track; the active side lights up.
 */
export function LiveSortSwitch({ value, onChange, className }: LiveSortSwitchProps) {
  const { t } = useTranslation();
  const timeLabel = t('Time', { defaultValue: 'Time' });
  const competitionLabel = t('Competition', { defaultValue: 'Competition' });
  const isTime = value === 'Time';

  return (
    <div
      role="group"
      aria-label={t('SortBy', { defaultValue: 'Sort by' })}
      className={cn('flex shrink-0 flex-col items-center gap-0.5', className)}
    >
      <div
        className={cn(
          'flex h-[22px] w-[50px] items-center justify-between rounded-full',
          'border border-border-default bg-bg-canvas px-[3px]',
        )}
      >
        <Knob
          pressed={isTime}
          label={timeLabel}
          onClick={() => onChange('Time')}
        />
        <Knob
          pressed={!isTime}
          label={competitionLabel}
          onClick={() => onChange('Competition')}
        />
      </div>
      {/* Keep both labels in layout (one invisible) so width never jumps. */}
      <span className="grid justify-items-center text-[10px] font-medium leading-none whitespace-nowrap text-text-secondary">
        <span
          className={cn('col-start-1 row-start-1', !isTime && 'invisible')}
          aria-hidden={!isTime}
        >
          {timeLabel}
        </span>
        <span
          className={cn('col-start-1 row-start-1', isTime && 'invisible')}
          aria-hidden={isTime}
        >
          {competitionLabel}
        </span>
      </span>
    </div>
  );
}

function Knob({
  pressed,
  label,
  onClick,
}: {
  pressed: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'size-[14px] shrink-0 rounded-full transition-colors',
        pressed
          ? 'bg-white/55 shadow-[0_0_4px_rgba(0,0,0,0.25)]'
          : 'bg-transparent hover:bg-white/15',
      )}
    />
  );
}
